import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useCan } from '../../shell/Can.js';
import { Button } from '../../../../shared/components/Button.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import {
  brandingLineMeasure,
  type BrandingLineMeasure,
} from '../../../../shared/contracts/branding-line.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import {
  getWorkOrder,
  submitWorkOrder,
  type WorkOrderDetail,
  type WorkOrderLine,
  type WorkOrderLineAttributeValue,
} from '../../api/brandingWorkOrderApi.js';
import {
  useWorkOrderBudget,
  WorkOrderBudgetWarning,
  workOrderSubmittedMessage,
} from './WorkOrderBudgetWarning.js';

/**
 * Work order detail — the complete picture of one order, and the place a
 * saved draft is sent for approval from.
 *
 * The list only carries number, vendor, FY, validity, status and amount; an
 * approver or anyone chasing an order needs the full picture: what branding
 * (ATL / BTL / POP…), which sub-type and media, the geography, the committed
 * quantity and dimensions, the agreed rate, every attribute, and the money.
 * All of it already comes back from GET /v1/m17/work-orders/:id.
 */
function dash(v: string | number | null | undefined): string {
  return v !== null && v !== undefined && String(v).trim() !== '' ? String(v) : '\u2014';
}

/**
 * A line with no budget behind it says why, in words.
 *
 * The codes are the database's own (t620_budget_match_chk); this only puts
 * them into language, and never invents one the column cannot hold.
 */
function fundedLabel(code: string | null | undefined): string {
  if (code === 'UNFUNDED') return 'No budget drawn';
  if (code === 'AMBIGUOUS') return 'More than one budget';
  if (code === 'UNASSESSED') return 'Not yet judged';
  return '\u2014';
}

function attrValue(
  a: WorkOrderLineAttributeValue,
  decimal: (v: string | number | null | undefined) => string,
  day: (v: string | null | undefined) => string,
): string {
  return (
    a.optionName ??
    a.valueText ??
    (a.valueNumber !== null && a.valueNumber !== undefined ? decimal(a.valueNumber) : null) ??
    (a.valueDate ? day(a.valueDate) : null) ??
    '\u2014'
  );
}

/**
 * The stored line read through the shared contract: how many pieces, of what,
 * and — for area work — the area they make, in the line's own unit. The unit
 * names are the tenant's (t602); a code the tenant has not named is shown
 * through the shared unit labels.
 */
function measureOf(l: WorkOrderLine): BrandingLineMeasure {
  return brandingLineMeasure(
    {
      quantity: Number(l.quantity),
      dim1: Number(l.dim1 ?? 1),
      dim2: Number(l.dim2 ?? 1),
      dim3: l.dim3 === null || l.dim3 === undefined ? null : Number(l.dim3),
      rateValue: Number(l.rateValue),
    },
    {
      uomName: l.uomName ?? unitLabel(l.unitOfMeasure),
      dim3UomName: l.dim3UomName ?? (l.dim3UomCode ? unitLabel(l.dim3UomCode) : null),
    },
  );
}

/** Area work carries real height × width; broadcast work a slot length. */
function dimsLabel(
  l: WorkOrderLine,
  m: BrandingLineMeasure,
  decimal: (v: string | number | null | undefined) => string,
): string {
  const parts: string[] = [];
  if (m.shape === 'AREA') parts.push(`${decimal(l.dim1)} \u00d7 ${decimal(l.dim2)}`);
  if (m.slot !== null && m.slot !== 0) parts.push(`slot ${decimal(m.slot)} ${m.slotUnit}`);
  return parts.length ? parts.join(' \u00b7 ') : '\u2014';
}

export function WorkOrderDetailPage(): ReactNode {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const { token } = useAuth();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money: groupMoney, decimal } = useBusinessNumber();
  const { dayRange, day } = useBusinessDay();
  // The screen prints a rupee sign and an em dash for anything unreadable;
  // only the grouping of the digits was ever the locale's business, and that
  // is now the tenant's. Kept local because the ₹ belongs to this screen's
  // layout, not to the shared formatter — M00 fixes the launch currency, so a
  // symbol inside the formatter would be doubled by every other caller.
  const money = (v: string | number | null | undefined): string => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? `\u20b9 ${groupMoney(n)}` : '\u2014';
  };
  const [wo, setWo] = useState<WorkOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error`, which replaces the page: a refused submission must
  // leave the order on screen beside the reason.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const budget = useWorkOrderBudget();
  // Sending a draft on changes an existing order, so it is the page's Edit
  // grant (Role Manager, BA_WORK_ORDERS) that decides who is offered it — the
  // Branding Officer who prepares drafts, not the Head who approves them. The
  // server's M17_WORK_ORDER_SUBMIT remains the real boundary.
  const maySubmit = useCan('/admin/work-orders', 'edit');

  useEffect(() => {
    if (!workOrderId) return;
    let alive = true;
    getWorkOrder(workOrderId, token)
      .then((d) => { if (alive) setWo(d); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [workOrderId, token]);

  async function submit(order: WorkOrderDetail): Promise<void> {
    setBusy(true);
    setSubmitError(null);
    setNotice(null);
    budget.clear();
    try {
      // The answer is the order as it now stands, so the status, and each
      // line's budget match, refresh from it without a second read.
      const submitted = await submitWorkOrder(order.workOrderId, token);
      const { budgetStatus, ...after } = submitted;
      setWo(after);
      budget.record(budgetStatus);
      setNotice(workOrderSubmittedMessage(order.woNumber));
    } catch (e) {
      budget.recordFailure(e);
      setSubmitError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return (
    <main aria-label="work-order-detail">
      <p><Link to="/admin/work-orders">{'\u2190'} Back to work orders</Link></p>
      <p role="alert">{error}</p>
    </main>
  );
  if (!wo) return <p>Loading{'\u2026'}</p>;

  const header: Array<[string, ReactNode]> = [
    ['Subject', dash(wo.subject)],
    ['Vendor', `${wo.vendorName}${wo.vendorCode ? ` (${wo.vendorCode})` : ''}`],
    ['Financial year', dash(wo.cycleCode)],
    ['Validity', dayRange(wo.validFromDate, wo.validToDate)],
    ['Status', <span key="st" style={statusStyle(wo.woStatusCode)}>{wo.woStatusCode}</span>],
    ['Total amount', money(wo.totalAmount)],
    ['Other expenses allowed', wo.otherExpensesAllowed ? 'Yes' : 'No'],
    ['Remarks', dash(wo.remarks)],
  ];

  return (
    <main aria-label="work-order-detail">
      <p><Link to="/admin/work-orders">{'\u2190'} Back to work orders</Link></p>
      <h1 style={{ marginBottom: 4 }}>{wo.woNumber}</h1>
      {submitError ? <p role="alert">{submitError}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      <WorkOrderBudgetWarning warning={budget.warning} />
      {wo.woStatusCode === 'DRAFT' && maySubmit ? (
        <p>
          <Button
            type="button"
            variant="primary"
            onClick={() => void submit(wo)}
            disabled={busy}
            data-testid="wo-submit"
          >
            Submit for approval
          </Button>
        </p>
      ) : null}

      <section aria-label="work-order-header">
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.25rem 1.5rem', margin: '0.5rem 0 1.5rem' }}>
          {header.map(([label, value]) => (
            <div key={label} style={{ display: 'contents' }}>
              <dt style={DT}>{label}</dt>
              <dd style={DD}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-label="work-order-lines">
        <h2 style={{ fontSize: '1rem' }}>Lines ({wo.lines.length})</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['#', 'Branding', 'Sub-type / media', 'Geography', 'Qty', 'Dims', 'Measured', 'Rate', 'Additional', 'HSN/SAC', 'Funded by', 'Line amount'].map((h) => (
                  <th key={h} style={TH}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wo.lines.map((l, i) => {
                const m = measureOf(l);
                return (
                <tr key={l.workOrderLineId ?? i} data-testid={`wo-line-${i}`}>
                  <td style={TD}>{l.lineNumber ?? i + 1}</td>
                  <td style={TD}>{dash(l.typeName)}</td>
                  <td style={TD}>
                    {dash(l.itemName ?? l.otherDetail)}
                    {l.variantName ? <div style={SUB}>{l.variantName}</div> : null}
                    {l.lineDescription ? <div style={SUB}>{l.lineDescription}</div> : null}
                    {l.attributes && l.attributes.length > 0 ? (
                      <div style={SUB}>
                        {l.attributes.map((a) => (
                          <span key={a.attributeId} style={{ marginRight: 8 }}>
                            <strong>{a.attributeName ?? a.attributeCode}:</strong> {attrValue(a, decimal, day)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td style={TD}>{dash(l.geoNodeName ?? l.geoNodeCode ?? l.geoLevelCode)}</td>
                  <td style={TD} data-testid={`wo-line-qty-${i}`}>{decimal(m.count)}{m.countUnit ? ` ${m.countUnit}` : ''}</td>
                  <td style={TD}>{dimsLabel(l, m, decimal)}</td>
                  <td style={TD} data-testid={`wo-line-measured-${i}`}>
                    {m.measured === null ? '\u2014' : `${decimal(m.measured)} ${m.measuredUnit}`}
                  </td>
                  <td style={TD}>{money(l.rateValue)}</td>
                  <td style={TD}>
                    {money(l.additionalAmount)}
                    {l.additionalRemark ? <div style={SUB}>{l.additionalRemark}</div> : null}
                  </td>
                  <td style={TD}>{dash(l.hsnSacCode)}</td>
                  {/* Which budget pays for this line, at the depth that budget
                      was drawn. The four columns behind this have existed since
                      m17_040 and were read by nothing until now. */}
                  <td style={TD}>
                    {l.budgetMatchCode === 'MATCHED' || l.budgetMatchCode === 'TRANSFER'
                      ? dash(l.fundingPlanName)
                      : fundedLabel(l.budgetMatchCode)}
                    {(l.budgetMatchCode === 'MATCHED' || l.budgetMatchCode === 'TRANSFER') && l.fundingBucketLabel ? (
                      <div style={SUB} data-testid={`wo-line-bucket-${i}`}>{l.fundingBucketLabel}</div>
                    ) : null}
                    {l.budgetExceptionReason ? (
                      <div style={SUB}>{l.budgetExceptionReason}</div>
                    ) : null}
                  </td>
                  <td style={{ ...TD, fontWeight: 600 }}>{money(l.lineAmount)}</td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...TD, fontWeight: 600 }} colSpan={11}>Total</td>
                <td style={{ ...TD, fontWeight: 700 }}>{money(wo.totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </main>
  );
}

function statusStyle(status: string): CSSProperties {
  const ok = status === 'APPROVED';
  const bad = status === 'REJECTED' || status === 'CANCELLED';
  const colour = ok ? 'var(--color-success, #137333)' : bad ? 'var(--color-danger, #c5221f)' : 'var(--color-text-muted, #5f6368)';
  return {
    display: 'inline-block',
    padding: '0.1rem 0.5rem',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--color-text-inverse, #fff)',
    background: colour,
  };
}

const DT: CSSProperties = {
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-muted, #5f6368)',
};
const DD: CSSProperties = { margin: 0 };
const TH: CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '2px solid var(--color-border, #dadce0)',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap',
};
const TD: CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid var(--color-border, #dadce0)',
  verticalAlign: 'top',
};
const SUB: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted, #5f6368)',
  marginTop: 2,
};
